import { useCallback } from 'react'
import { useAppSelector } from '@/app/hooks'

/**
 * Terminology system. Tenants can override default labels (e.g. a pharma
 * tenant calls "Customers" -> "HCPs"; a construction tenant calls them
 * "Clients"). The overrides are loaded with the company profile.
 *
 * Until the company terminology endpoint is wired (Batch P5), this falls
 * back to a built-in default dictionary keyed by industry template.
 */

const DEFAULT_TERMS: Record<string, string> = {
  customers: 'Customers',
  customer: 'Customer',
  orders: 'Orders',
  visits: 'Visits',
  reps: 'Sales Reps',
  beats: 'Beats',
  products: 'Products',
  invoices: 'Invoices',
  pharma_reports: 'Pharma Reports',
  visit_cooldown_rules: 'Visit Cooldown Rules',
  visit_overrides: 'Override Requests',
}

// Industry-specific overrides (subset; full set comes from backend later)
const INDUSTRY_TERMS: Record<string, Record<string, string>> = {
  pharma_dist: { customers: 'HCPs', customer: 'HCP', visits: 'Detailing Visits' },
  pharma_mfr: { customers: 'HCPs', customer: 'HCP' },
  vet_pharma: { customers: 'Vet HCPs', customer: 'Vet HCP' },
  construction: { customers: 'Clients', customer: 'Client', orders: 'Project Orders' },
  agriculture: { customers: 'Farmers', customer: 'Farmer' },
  horeca: { customers: 'Outlets', customer: 'Outlet' },
  field_services: { customers: 'Clients', customer: 'Client' },
}

export function useTerm() {
  const company = useAppSelector((s) => s.auth.company)
  const template = company?.industry_template_code ?? ''

  return useCallback(
    (key: string): string => {
      const industryOverride = INDUSTRY_TERMS[template]?.[key]
      if (industryOverride) return industryOverride
      return DEFAULT_TERMS[key] ?? toTitle(key)
    },
    [template],
  )
}

function toTitle(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}
